import { useId, useMemo, type ReactNode } from "react";

import { ShieldCheck, Info } from "lucide-react";

import type { TaxonomyPayload } from "../../lib/api";

import { type LocationPickerDraft } from "../../components/LocationPicker";
import { LocationPicker } from "../../components/LocationPicker";

import { ImageUploader } from "../../components/ImageUploader";

import { CompanyMediaPreview } from "../../components/CompanyMediaPreview";

import { Req } from "../../components/ui/kit";

import "../../components/CompanyManagementMobile.css";
import { StepProps } from "./model";
import { SecHead, OptionalSection, CheckChips } from "./FieldControls";
export function StepBasics({ form, set, issue, companyId, logoFile, setLogoFile, cover, showExtras, part }: StepProps & { companyId: string; logoFile: File | null; setLogoFile: (f: File | null) => void; cover: ReactNode; showExtras: boolean; part?: "details" | "branding" }) {
  const fieldId = useId();
  const logoImages = useMemo(() => logoFile ? [logoFile] : [], [logoFile]);
  return (
    <div className="space-y-5">
      {part !== "branding" && <><SecHead title="What's your business called?" sub="Use the business name shown on the registration proof you will submit." />
      <div>
        <label htmlFor={`${fieldId}-name`} className="label">Business name <Req /></label>
        <input id={`${fieldId}-name`} data-company-field="name" className="field" value={form.name} maxLength={200} aria-invalid={issue?.field === "name"} aria-describedby={issue?.field === "name" ? `${fieldId}-error` : undefined} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Metro Build Construction Corp." />
        {issue?.field === "name" && <p id={`${fieldId}-error`} role="alert" className="text-xs text-brand-red mt-2">{issue.message}</p>}
      </div></>}
      {part !== "details" && <OptionalSection title="Branding">
      {showExtras && <div>
        <label htmlFor={`${fieldId}-tagline`} className="label">Tagline (optional)</label>
        <input id={`${fieldId}-tagline`} className="field" value={form.tagline} maxLength={255} onChange={(e) => set("tagline", e.target.value)} placeholder="A short line describing what you do" />
      </div>}
      <div>
        <p className="label">Business logo (optional)</p>
        <ImageUploader value={logoImages} onChange={files => setLogoFile(files[0] || null)} min={0} max={1} mediaKind="logo" layout="dropzone" example="Choose your business logo. Saved when you continue to the next step." />
        {!logoFile && companyId && <div className="mt-3">
          <CompanyMediaPreview companyId={companyId} kind="logo" alt="Current business logo" className="h-24 w-24 rounded-lg border border-line bg-navy-50 object-contain" />
          <p className="mt-1 text-xs text-mut">Current logo. Choose a new image above to replace it.</p>
        </div>}
      </div>
      {showExtras && cover}
      </OptionalSection>}
    </div>
  );
}

export function StepLocation({ tax, form, set, locationDraft, setLocationDraft, showExtras }: StepProps & { locationDraft: LocationPickerDraft; setLocationDraft: (value: LocationPickerDraft) => void; showExtras: boolean }) {
  const fieldId = useId();
  return <div className="space-y-5">
      <SecHead title="Where is your business based?" sub="Add a location to help people find you, or continue and add it later." />
        <div>
          {showExtras ? <>
            <p className="label">City / Location (optional)</p>
            <LocationPicker draft={locationDraft} onDraftChange={setLocationDraft} value={form.city} onChange={(display, parts) => set("city", parts?.city || display)} />
          </> : <>
            <label htmlFor={`${fieldId}-city`} className="label">City / Municipality (optional)</label>
            <input id={`${fieldId}-city`} className="field" value={form.city} maxLength={120} onChange={event => set("city", event.target.value)} placeholder="e.g. Quezon City" />
          </>}
        </div>
        {showExtras && <OptionalSection title="Office or branch details">
        <div>
          <label htmlFor={fieldId} className="label">This profile represents (optional)</label>
          <select id={fieldId} className="field" value={form.profileRepresents} onChange={(e) => set("profileRepresents", e.target.value)}>
            <option value="">Select…</option>
            {tax.profileRepresents.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        </OptionalSection>}
    </div>;
}

export function StepProfile({ tax, form, set, issue, selectedType, subtypeOther, setSubtypeOther, showExtras, retainedSubtype, part }: StepProps & { selectedType: TaxonomyPayload["organizationTypes"][number] | null; subtypeOther: string; setSubtypeOther: (v: string) => void; showExtras: boolean; retainedSubtype: string; part?: "details" | "classification" }) {
  const fieldId = useId();
  const c = tax.classification;
  const subtypeOptions = selectedType?.subtypeGroups.flatMap(group => group.options) || [];
  const legacySelected = Boolean(form.subtype && form.subtype === retainedSubtype && !subtypeOptions.includes(form.subtype));
  return (
      <div className="space-y-5">
        {part !== "classification" && <><SecHead title="How is your business organized?" sub="Choose your organization type. Where subtypes apply, choose the one that fits." />
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${fieldId}-type`} className="label">Organization type <Req /></label>
            <select id={`${fieldId}-type`} data-company-field="organizationType" className="field" value={form.organizationType} aria-invalid={issue?.field === "organizationType"} aria-describedby={issue?.field === "organizationType" ? `${fieldId}-error` : undefined}
              onChange={(e) => { set("organizationType", e.target.value); set("subtype", ""); }}>
              <option value="">Select organization type…</option>
              {tax.organizationTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {(subtypeOptions.length > 0 || form.subtype || !selectedType) && <div>
            <label htmlFor={`${fieldId}-subtype`} className="label">Subtype {subtypeOptions.length > 0 ? <Req /> : "(optional)"}</label>
            <select id={`${fieldId}-subtype`} data-company-field="subtype" className="field" value={form.subtype} disabled={!selectedType} aria-invalid={issue?.field === "subtype"} aria-describedby={issue?.field === "subtype" ? `${fieldId}-error` : undefined}
              onChange={(e) => set("subtype", e.target.value)}>
              <option value="">{selectedType ? "Select subtype…" : "Choose a type first"}</option>
              {legacySelected && <option value={form.subtype}>{form.subtype} (previously saved)</option>}
              {selectedType?.subtypeGroups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </optgroup>
              ))}
            </select>
            {legacySelected && <p className="mt-2 text-xs text-mut">Your previously saved choice is retained. You can replace it with a current subtype.</p>}
          </div>}
          {/* v3.9.67 (QA #2) — when the chosen subtype is an "Other — specify", show a field to type it. */}
          {/other|specify/i.test(form.subtype) && (
            <div className="sm:col-span-2">
              <label htmlFor={`${fieldId}-other`} className="label">Specify organization type <Req /></label>
              <input id={`${fieldId}-other`} className="field" value={subtypeOther} onChange={(e) => setSubtypeOther(e.target.value)} placeholder="Describe your organization type" maxLength={120} />
            </div>
          )}
        </div>
        {(issue?.field === "organizationType" || issue?.field === "subtype") && <p id={`${fieldId}-error`} role="alert" className="text-xs text-brand-red">{issue.message}</p>}

        {selectedType && (selectedType.verification.length > 0 || selectedType.rules.length > 0) && (
          <details className="border-t border-line pt-4">
          <summary className="cursor-pointer rounded py-2 text-sm font-semibold text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy">Registration guidance</summary>
          <div className="space-y-4 pt-4">
          <p className="text-sm text-mut">Submit registration proof on Review. Your business stays private and marketplace actions stay locked until Admin approves verification.</p>
            {selectedType.verification.length > 0 && (
              <>
                <div className="font-semibold text-navy flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-brand-green" />Verification documents for this type</div>
                <ul className="list-disc ml-5 mt-1 text-mut space-y-0.5">{selectedType.verification.map((x) => <li key={x}>{x}</li>)}</ul>
              </>
            )}
            {selectedType.rules.length > 0 && (
              <div className="mt-3 flex gap-1.5 text-mut"><Info className="w-4 h-4 shrink-0 mt-0.5 text-navy" /><div>{selectedType.rules.join(" ")}</div></div>
            )}
          </div>
          </details>
        )}</>}
      {showExtras && part !== "details" && <OptionalSection title="Business classifications">
        <div>
          <label className="label">Ownership</label>
          <CheckChips options={c.ownership} value={form.ownershipClassifications} onChange={(v) => set("ownershipClassifications", v)} />
        </div>
        <div>
          <label className="label">Enterprise classification</label>
          <CheckChips options={c.enterpriseClassification} value={form.enterpriseClassifications} onChange={(v) => set("enterpriseClassifications", v)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${fieldId}-relationship`} className="label">Corporate relationship (optional)</label>
            <select id={`${fieldId}-relationship`} className="field" value={form.corporateRelationship} onChange={(e) => set("corporateRelationship", e.target.value)}>
              <option value="">Select…</option>
              {c.corporateRelationship.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor={`${fieldId}-status`} className="label">Operating status (optional)</label>
            <select id={`${fieldId}-status`} className="field" value={form.operatingStatus} onChange={(e) => set("operatingStatus", e.target.value)}>
              <option value="">Select…</option>
              {c.operatingStatus.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <p className="mt-2 text-xs text-mut">Operating status affects verification. Active, Newly registered or an unspecified status can be eligible; other statuses remove verification and lock marketplace actions.</p>
          </div>
        </div>
      </OptionalSection>}
      </div>
  );
}
